import Vue from "vue";
import { reactive, toRefs, provide, inject } from "@vue/composition-api";
import { actions as reportActions } from "@/store/modules/report";
import { actions as timerActions } from "@/store/modules/timer";
import { actions as appActions, state as appState } from "@/store/modules/app";
import { actions as taskActions, state as taskState } from "@/store/modules/task";
import { actions as taskButtonActions, state as taskButtonState } from "@/store/modules/task_button";
// import {syncItemToList} from "@/plugins/formatting";

const PROTOCOL = process.env.VUE_APP_WS_PROTOCOL ? process.env.VUE_APP_WS_PROTOCOL : "";
const HOST = process.env.VUE_APP_HOST ? process.env.VUE_APP_HOST : "";
const BASE_URL = `${PROTOCOL}://${HOST}`;

var tasks_ws = null;

export const state = reactive({
    reconnect_counter: 0,
    taskDeleteCallbacks: [],
    taskChangeCallbacks: []
});

let notifyTaskDelete = (fx) => {
    for (const callback of state.taskDeleteCallbacks) {
        callback(fx)
    }
}

let notifyTaskChange = (task) => {
    for (const callback of state.taskChangeCallbacks) {
        callback(task)
    }
}

let connectWs = function () {
    tasks_ws = new WebSocket("{0}/ws/tasks".format(BASE_URL));

    tasks_ws.onopen = function () {
        console.log("Tasks WebSocket: connected");
        state.reconnect_counter = 0;
    };

    tasks_ws.onmessage = function (ev) {
        //console.log("Tasks WebSocket: incoming message", ev.data);
        let data = JSON.parse(ev.data);

        // On task deleted
        if (data.deleted !== undefined && Array.isArray(data.deleted)) {
            for (let i in data.deleted) {
                let deleted_id = data.deleted[i];
                let deleted_fx = null;

                if (typeof deleted_id == "number") {
                    // deletion by task id
                    deleted_fx = x => x.id === deleted_id;
                } else if (deleted_id.endsWith("_*")) {
                    // delete all occurrences of task template
                    deleted_id = deleted_id.substr(0, deleted_id.length - 1);
                    deleted_fx = function (x) {
                        return (
                            x.virtual_id != null &&
                            typeof x.virtual_id == "string" &&
                            x.virtual_id.startsWith(deleted_id)
                        );
                    };
                } else {
                    // delete virtual task
                    deleted_fx = x => x.virtual_id === deleted_id;
                }

                if (taskState.current_task != null) {
                    if (deleted_fx(taskState.current_task)) {
                        taskActions.closeTask();
                    }
                }
                Vue.remove_item_from_list(taskState.tasks_list, deleted_fx);
                Vue.remove_item_from_list(taskState.calendar_tasks_list, deleted_fx);
                Vue.remove_item_from_list(taskState.recurring_tasks_list, deleted_fx);
                reportActions.onWsTaskDelete(deleted_fx);
                timerActions.onWsTaskDelete(deleted_fx);
                actions.notifyTaskDelete(deleted_fx)
            }
        }

        // Remove by task property (client is deleted then all tasks with that "client_id" are removed)
        if (data.remove_by_property !== undefined && Array.isArray(data.remove_by_property)) {
            for (let i in data.remove_by_property) {
                let prop = data.deleted[i];
                let deleted_fx = x => x[prop.name] === prop.val;
                Vue.remove_item_from_list(taskState.tasks_list, deleted_fx);
                Vue.remove_item_from_list(taskState.calendar_tasks_list, deleted_fx);
                reportActions.onWsTaskDelete(deleted_fx);
                timerActions.onWsTaskDelete(deleted_fx);
                actions.notifyTaskDelete(deleted_fx)
            }
        }

        // On task change (assignee change, date change, status change, title change ..)
        if (data.changed !== undefined && Array.isArray(data.changed)) { // !== undefined) {
            for (let t of data.changed) {
                if (taskState.current_task != null) {
                    if (taskState.current_task.id === t.id || taskState.current_task.virtual_id === t.virtual_id) {
                        Vue.sync_item_changes(taskState.current_task, t);
                    }
                }
                taskActions.syncTaskAndRecurrenceChangesToLists(t);
                reportActions.onWsTaskChange(t);
                timerActions.onWsTaskChange(t);
                actions.notifyTaskChange(t)
                // console.info('syncItemToList :: taskState.calendar_tasks_list', taskState.calendar_tasks_list)

                // TODO :: backend must not push tasks to deactivated users, users who have no access to that client
                // TODO :: person A with Task, person B changes Task date, then A does not see change on calendar (vice versa works)
                // FIXME :: syncing changes to calendar is broken:
                // syncItemToList(taskState.calendar_tasks_list, t);
            }
        }

        // On task (log, file) added to task
        if (data.append !== undefined && Array.isArray(data.append) && taskState.current_task != null) {
            for (let i in data.append) {
                let item = data.append[i];
                if (taskState.current_task.id === item.id || taskState.current_task.virtual_id === item.virtual_id) {
                    let prop_name = item.logs !== undefined ? "logs" : "files";
                    for (let j = 0; j < item[prop_name].length; j++) {
                        if (item[prop_name][j].is_deleted === 1) {
                            Vue.remove_from_list(taskState.current_task[prop_name], item[prop_name][j].id);
                            if (taskState.current_task.logs) {
                                for (const logIndex in taskState.current_task.logs) {
                                    if (taskState.current_task.logs[logIndex].files) {
                                        Vue.remove_from_list(taskState.current_task.logs[logIndex].files, item[prop_name][j].id);
                                    }
                                }
                            }

                        } else {
                             if (item[prop_name][j].related_type === 'task_log') {
                                 for (const logIndex in taskState.current_task.logs) {
                                     if (taskState.current_task.logs[logIndex].id === item[prop_name][j].related_id) {
                                         if (!taskState.current_task.logs[logIndex].files) {
                                             taskState.current_task.logs[logIndex].files = []
                                         }

                                         Vue.sync_to_list(taskState.current_task.logs[logIndex].files, item[prop_name][j]);
                                     }
                                 }
                             } else {
                                 Vue.sync_to_list(taskState.current_task[prop_name], item[prop_name][j]);
                             }
                        }
                    }
                }
            }
        }
    };

    tasks_ws.onclose = function () {
        console.log("Tasks WebSocket: connection lost");
        if (state.reconnect_counter >= appState.reconnect_max_tries) {
            appActions.showGlobalConnectionLost();
        } else {
            state.reconnect_counter++;
            setTimeout(function () {
                console.log("Tasks WebSocket: reconnecting...({0})".format(state.reconnect_counter));
                connectWs();
            }, appState.reconnect_wait);
        }
    };
};

const addTaskDeleteCallback = (callback) => {
    state.taskDeleteCallbacks.push(callback)
}

const addTaskChangeCallback = (callback) => {
    state.taskChangeCallbacks.push(callback)
}

export const actions = {
    connectWs: connectWs,
    addTaskDeleteCallback,
    addTaskChangeCallback,
    notifyTaskDelete,
    notifyTaskChange
};

export const key = Symbol("TasksWsStore");

export const provideTaskWs = () => {
    provide(key, {
        ...toRefs(state),
        ...actions
    });
};

appState.web_socket_connectors.push(actions.connectWs);

export default () => inject(key);
